// Шторка выбора модели из шапки чата (1.25.7): названия, группы по провайдерам,
// недавние модели. Каталог и состояния приходят из /api/models (единый источник —
// backend); здесь только представление.
import { TEXT } from './ui-text.js';

// Порядок групп — как в строке моделей и цепочке ответа.
export const PROVIDER_ORDER = ['openai', 'zai', 'yandex', 'home'];
export const RECENT_KEY = 'petrovich:recent-models';
const RECENT_LIMIT = 3;

export function modelLabel(id) {
  if (!id) {
    return '';
  }
  const known = TEXT.modelPicker.names[id];
  if (known) {
    return known;
  }
  const name = String(id).split(':').slice(1).join(':').replace(/\/latest$/, '');
  return name ? humanizeModelName(name) : String(id);
}

// Этап 33: название новой модели провайдера без правки словаря — из имени:
// «gpt-6.1-sol» → «GPT-6.1 Sol», «yandexgpt-5-pro» → «YandexGPT 5 Pro»,
// «qwen3.6-35b-a3b» → «Qwen3.6 35B A3B». Имена с «:» или «/» — как есть.
const BRANDS = { gpt: 'GPT', glm: 'GLM', yandexgpt: 'YandexGPT', aliceai: 'AliceAI', deepseek: 'DeepSeek', llm: 'LLM', oss: 'OSS' };
// За ними версия идёт через дефис: «GPT-6.1», «GLM-5.3».
const HYPHEN_BRANDS = new Set(['gpt', 'glm']);

function humanizeWord(word) {
  const lower = word.toLowerCase();
  if (BRANDS[lower]) {
    return BRANDS[lower];
  }
  if (/^o\d+$/.test(lower)) {
    return lower;
  }
  if (/^(\d+(\.\d+)?[bkm]|a\d+b|fp\d+)$/.test(lower)) {
    return lower.toUpperCase();
  }
  if (/^v\d/.test(lower)) {
    return `V${lower.slice(1)}`;
  }
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function humanizeModelName(name) {
  if (/[:/]/.test(name)) {
    return name;
  }
  const words = name.split('-').filter(Boolean);
  const result = [];
  words.forEach((word, index) => {
    const previous = words[index - 1]?.toLowerCase();
    if (index === 1 && HYPHEN_BRANDS.has(previous) && /^\d/.test(word)) {
      result[0] = `${result[0]}-${word}`;
      return;
    }
    result.push(humanizeWord(word));
  });
  return result.join(' ');
}

export function providerLabel(provider) {
  return TEXT.brain.providerNames[provider] || provider;
}

// Момент для шторок «Состояние мозга» и «Модели провайдеров»: сегодня — время,
// иначе ещё и дата.
export function formatMoment(ts, now = new Date()) {
  if (!ts) {
    return '';
  }
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  const time = date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  const sameDay = date.toDateString() === now.toDateString();
  return sameDay ? time : `${date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} ${time}`;
}

// Этап 33.2: склонение по числу — формы «одна, две, пять» (1 чат, 2 чата, 5 чатов).
export function pluralRu(count, forms) {
  const n = Math.abs(count) % 100;
  const last = n % 10;
  if (n > 10 && n < 20) {
    return forms[2];
  }
  if (last === 1) {
    return forms[0];
  }
  return last >= 2 && last <= 4 ? forms[1] : forms[2];
}

// «3 чата, 1 шаблон и настройки 2 человек перейдут на GPT-5.6 Terra» — что
// выключение модели сделает с её чатами; пусто, если на модели ничего нет.
export function disableConsequence(item, defaultModelId) {
  const t = TEXT.modelCatalog;
  const parts = [];
  // «1 чат перейдёт», «2 чата перейдут», «настройки 1 человека перейдут».
  let singular = false;
  if (item?.chats > 0) {
    parts.push(`${item.chats} ${pluralRu(item.chats, t.forms.chats)}`);
    singular = pluralRu(item.chats, t.forms.chats) === t.forms.chats[0];
  }
  if (item?.templates > 0) {
    parts.push(`${item.templates} ${pluralRu(item.templates, t.forms.templates)}`);
    singular = pluralRu(item.templates, t.forms.templates) === t.forms.templates[0];
  }
  if (item?.users > 0) {
    parts.push(`${t.usersPrefix} ${item.users} ${pluralRu(item.users, t.forms.users)}`);
    singular = false;
  }
  if (!parts.length) {
    return '';
  }
  const list = parts.length > 1 ? `${parts.slice(0, -1).join(', ')} ${t.and} ${parts[parts.length - 1]}` : parts[0];
  const verb = parts.length === 1 && singular ? t.moveToOne : t.moveTo;
  return `${list} ${verb} ${modelLabel(defaultModelId)}.`;
}

// Текст отказа PATCH /api/models/catalog: провал пробы — по причине, иначе по коду.
export function catalogErrorText(error) {
  const t = TEXT.modelCatalog;
  const detail = error?.detail && typeof error.detail === 'object' ? error.detail : null;
  if (detail?.code === 'check_failed') {
    return `${t.checkFailedAt} ${t.checkErrors[detail.reason] || t.checkErrors.rejected}`;
  }
  return t.errors[detail?.code] || t.errors.failed;
}

// Этап 33: группы шторки «Модели провайдеров». Сверху новые, затем основная и
// включённые, затем остальные, пропавшие внизу. Спрятанные правилами — отдельно,
// если модель не используется (не включена, без роли в конфигурации и без чатов).
function catalogRank(item) {
  if (item.isNew) {
    return 0;
  }
  if (item.isDefault) {
    return 1;
  }
  if (item.enabled) {
    return 2;
  }
  return item.missing ? 4 : 3;
}

export function catalogGroups(catalog) {
  return (catalog?.providers || []).map((group) => {
    const used = (item) => item.enabled || item.roles?.length || item.chats > 0;
    const sorted = [...(group.items || [])].sort(
      // Внутри ранга — свежие версии выше: «gpt-6.1-sol» раньше «gpt-5.6-terra».
      (a, b) => catalogRank(a) - catalogRank(b) || b.id.localeCompare(a.id, 'en', { numeric: true }),
    );
    return {
      ...group,
      title: providerLabel(group.provider),
      shown: sorted.filter((item) => !item.hidden || used(item)),
      hidden: sorted.filter((item) => item.hidden && !used(item)),
    };
  });
}

// [{provider, title, items}] — провайдеры в порядке PROVIDER_ORDER, прочие следом.
export function groupModels(models) {
  const groups = new Map();
  for (const model of models || []) {
    if (!groups.has(model.provider)) {
      groups.set(model.provider, []);
    }
    groups.get(model.provider).push(model);
  }
  const rank = (provider) => {
    const index = PROVIDER_ORDER.indexOf(provider);
    return index === -1 ? PROVIDER_ORDER.length : index;
  };
  return [...groups.keys()]
    .sort((a, b) => rank(a) - rank(b))
    .map((provider) => ({ provider, title: providerLabel(provider), items: groups.get(provider) }));
}

// Модель нельзя выбрать в этом чате: российская при флажке «не отдавать резерву».
export function isModelBlocked(model, noDomestic) {
  return Boolean(noDomestic && model?.domestic);
}

// Подпись под моделью: запрет флажком важнее состояния провайдера.
export function modelHint(model, noDomestic) {
  if (isModelBlocked(model, noDomestic)) {
    return TEXT.modelPicker.domesticHint;
  }
  if (model?.state && model.state !== 'ok') {
    return TEXT.modelPicker.states[model.state] || '';
  }
  return '';
}

export function loadRecentModels() {
  try {
    const parsed = JSON.parse(globalThis.localStorage?.getItem(RECENT_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((id) => typeof id === 'string') : [];
  } catch (_) {
    return [];
  }
}

export function rememberRecentModel(id) {
  const next = [id, ...loadRecentModels().filter((item) => item !== id)].slice(0, RECENT_LIMIT);
  try {
    globalThis.localStorage?.setItem(RECENT_KEY, JSON.stringify(next));
  } catch (_) {
    // localStorage недоступен — «Недавние» просто не запомнятся.
  }
  return next;
}

// Недавние, которые есть в каталоге; секция имеет смысл от двух моделей.
export function recentFromCatalog(recentIds, models) {
  const byId = new Map((models || []).map((model) => [model.id, model]));
  const items = (recentIds || []).map((id) => byId.get(id)).filter(Boolean);
  return items.length >= 2 ? items : [];
}
